import { Component, DoCheck, HostListener, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ResponseModel } from 'app/models/shared/response.model';
import { TooltipModel } from 'app/models/shared/tooltip.model';
import { LoggedUserService, UserService } from 'app/services';
import { EventService } from 'app/services/event.service';
import { SettingsService } from 'app/services/settings.service';
import { AppPageEnum, EventName, TooltipNameEnum } from 'app/shared/enums';
import { AppNotify } from 'app/utilities';

import { cloneDeep, isEqual } from 'lodash';
import { finalize } from 'rxjs';
import { ExecutiveUsersSpec } from 'specification';

@Component({
    selector: 'app-tooltip',
    templateUrl: './tooltip.component.html',
    styleUrl: './tooltip.component.scss',
    standalone: false,
})
export class TooltipComponent implements DoCheck {
  isVisible = false;
  tooltipLeft = 0;
  tooltipTop = 0;

  isAdmin = false;
  isSaving = false;
  isDirty = false;
  isEdit = false;
  tooltipData: TooltipModel;
  tooltipDataCloned: TooltipModel;
  companyId!: number;
  extraContent?: string;

  constructor(
    private loggedUserService: LoggedUserService
    , private userService: UserService
    , private executiveUserSpec: ExecutiveUsersSpec
    , private eventService: EventService
    , private settingService: SettingsService
    , private cdr: ChangeDetectorRef) {
      this.tooltipData = new TooltipModel();
      this.tooltipDataCloned = new TooltipModel();

      this.loggedUserService.currentUser.subscribe(u => {
        this.isAdmin = this.executiveUserSpec.IsSatisfiedBy(u?.user_role);
        this.companyId = u?.company_id as number;
      })

      this.eventService.on(EventName.ShowTooltip, (data?: TooltipModel) => {
        if (!data || !data.page || !data.name) {
          this.tooltipData = new TooltipModel();
          this.tooltipDataCloned = new TooltipModel();
          this.isVisible = false;
          this.cdr.markForCheck();
          return;
        };
        let tooltip = this.userService.getTooltip(data.page, data.name);
        if (tooltip) {
          this.tooltipData = new TooltipModel(data.elementTarget, tooltip);
        } else {
          this.tooltipData = data;
        }

        if (data.page !== AppPageEnum.ReconciliationReport && data.name !== TooltipNameEnum.ExtractionStatus) {
          this.extraContent = data.content;
        }

        // Don't show empty tooltips to non-admin users
        if (!this.isAdmin && !this.tooltipData.content && !data.content) {
          return;
        }

        this.tooltipDataCloned = cloneDeep(this.tooltipData);
        this.showAtTarget(data.elementTarget);
      });
  }

  private showAtTarget(target: HTMLElement | string) {
    let el: HTMLElement | null = null;
    if (typeof target === 'string') {
      el = document.querySelector(target);
    } else {
      el = target;
    }

    if (el) {
      const rect = el.getBoundingClientRect();
      this.tooltipLeft = rect.left + rect.width / 2 - 160; // center roughly
      this.tooltipTop = rect.top - 8; // above target
      // Ensure tooltip doesn't go off-screen
      if (this.tooltipLeft < 8) this.tooltipLeft = 8;
      if (this.tooltipTop < 8) this.tooltipTop = rect.bottom + 8;
    }

    this.isVisible = true;
    this.cdr.markForCheck();
  }

  handleMouseLeave() {
    if (!this.isAdmin) {
      this.hide();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isVisible && this.isAdmin) {
      const tooltipEl = document.querySelector('.fixed.z-\\[9999\\]');
      if (tooltipEl && !tooltipEl.contains(event.target as Node)) {
        this.hide();
      }
    }
  }

  private hide() {
    this.handleHidden();
  }

  ngDoCheck(): void {
    this.isDirty = isEqual(this.tooltipData, this.tooltipDataCloned);
  }

  switchToEditMode() {
    this.isEdit = !this.isEdit;
  }

  handleCancel() {
    this.tooltipData = cloneDeep(this.tooltipDataCloned);
    this.isEdit = false;
  }

  handleSubmit() {
    if (this.isSaving) return;

    if (!this.tooltipData.content) {
      return;
    }

    this.isSaving = true;
    this.settingService.saveTooltip(this.tooltipData.toRequest())
    .pipe(finalize(() => {
      this.isSaving = false;
      this.cdr.markForCheck();
    }))
    .subscribe((r: ResponseModel<TooltipModel>) => {
      if (r.status === 200) {
        this.tooltipData.id = r.data?.id;

        this.tooltipDataCloned = cloneDeep(this.tooltipData);
        const data = cloneDeep(this.tooltipData);

        this.userService.setTooltip(data);
        this.isEdit = false;
        AppNotify.success("Tooltip saved successfully!");
      }
    })
  }

  handleHidden() {
    this.tooltipData = cloneDeep(this.tooltipDataCloned);
    this.isEdit = false;
    this.extraContent = undefined;
    this.isVisible = false;
    this.cdr.markForCheck();
  }
}
